import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useSelectedRules } from "hooks/use-selected-rule";
import { PaymentCalculationRule, PaymentRule } from "types/payment";
import { WebRule } from "types/web-rule";

const hasChargeableAmount = (rule: WebRule): boolean =>
    rule.paymentAmount > 0 && rule.paymentCalculationRule !== PaymentCalculationRule.NoPayment;

const canChargePrepayment = (rule: WebRule): boolean =>
    rule.requireMenuPayment || hasChargeableAmount(rule);

const usePaymentRule = (): PaymentRule => {
    const allBookedRules = useSelectedRules();
    const reservation = useWebBooking();

    if (!allBookedRules) return PaymentRule.NoPayment;

    const ruleMap = new Map(
        reservation.data?.articles?.map((article) => [article.ruleId, article])
    );

    /**
     * The rules describe what *could* be charged; `chargeShouldPayAmount` is the total the
     * backend actually landed on for this booking. When that is 0 there is nothing to collect
     * up front, so the guest should book rather than be sent through a payment for 0.
     *
     * Only prepayment is gated on this. On a no show booking the guest always pays 0 up front -
     * there the amount is the fee that would be charged if they never turn up, so gating card
     * registration on it would disable no show handling entirely.
     */
    const hasAmountToCollect = (reservation.data?.chargeShouldPayAmount ?? 0) > 0;

    /**
     * Determines if prepayment is required based on menu payment rules.
     *
     * A booking requires prepayment if **both** conditions are met:
     * 1. A booked resource with a rule that requires menu payment.
     * 2. At least one booked menu-article that shares the same `menuGuid`
     *    as the booked resource article which requires menu-payment (i.e., an article linked to the rule).
     *
     * This ensures that menu-related items requiring payment trigger prepayment.
     */
    const requiresPrepaymentForSomeMenu = allBookedRules.some((rule) => {
        const requireMenuPayment = rule.requireMenuPayment;
        if (!requireMenuPayment) return false;
        const resourceArticle = ruleMap.get(rule.id);
        return (
            reservation.data?.nonTableArticles?.some(
                (article) => article.menuGuid === resourceArticle?.menuGuid
            ) ?? false
        );
    });

    if (requiresPrepaymentForSomeMenu && hasAmountToCollect) return PaymentRule.Prepayment;

    // If any booked rule requires prepayment - booking should require prepayment. Has a higher priority than noShow.
    if (
        hasAmountToCollect &&
        allBookedRules.some(
            (rule) => rule.paymentRule === PaymentRule.Prepayment && canChargePrepayment(rule)
        )
    ) {
        return PaymentRule.Prepayment;
    }

    // If any booked rule requires noShow - booking should require card registration.
    // A no show rule with no fee configured has nothing to charge if the guest does not
    // show up, so asking for card details would be friction with no purpose. Booking a mix
    // of rules still registers the card as long as one of them carries a fee.
    if (
        allBookedRules.some(
            (rule) => rule.paymentRule === PaymentRule.NoShowFee && hasChargeableAmount(rule)
        )
    ) {
        return PaymentRule.NoShowFee;
    }

    return PaymentRule.NoPayment;
};

export { usePaymentRule };
